"use client";

import { StyledLink } from "@/components/StyledLink";
import {
  Box,
  Container,
  Typography,
  Select,
  MenuItem,
  LinearProgress,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Stack,
  Link,
  CircularProgress,
  styled,
  List,
  ListItem,
  Pagination, // Import Pagination
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ExpandIcon as ExpandMore,
  ExternalLink,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import { apiRemote, IHost, IUserRoundStatus } from "@/api";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUser } from "@/contexts/UserContext";
import { useAlert } from "@/contexts/AlertContext";
import { useWorkshop } from "@/contexts/WorkshopContext"; 
import { Interface } from "readline";

const theme = createTheme({
  palette: {
    primary: {
      main: '#007bff',
    },
    secondary: {
      main: '#6c757d',
    },
    success: {
      main: '#28a745',
    },
    warning: {
      main: '#ffc107',
    },
    background: {
      default: '#f4f7f9',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h1: {
      fontSize: '28px',
      fontWeight: 700,
      color: '#333',
    },
    h3: {
      fontSize: '18px',
      fontWeight: 500,
    },
    h4: {
      fontSize: '16px',
      fontWeight: 500,
    },
    body1: {
      fontSize: '16px',
    },
    body2: {
      fontSize: '14px',
    },
    caption: {
      fontSize: '12px',
    },
  },
  shape: {
    borderRadius: 2,
  },
});

interface WidgetHeaderProps {
  title: string;
  viewAllLink?: string;
  titleColor?: string;
}

const WidgetHeader: React.FC<WidgetHeaderProps> = ({ title, viewAllLink, titleColor }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #eee',
      paddingBottom: 1.5,
      marginBottom: 2
    }}
  >
    <Typography variant="h5" sx={{ color: titleColor || 'text.primary' }}>
      {title}
    </Typography>
    {viewAllLink && (
      <Link href={viewAllLink} sx={{ fontSize: '14px', color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}>
        View All Workshops →
      </Link>
    )}
  </Box>
);

interface EduCatalystWidgetProps {
  allWorkshops: any[];
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  handleRegisterWorkshop: (workshop: any) => void;
  submittingWorkshopId: string | null;
  loading: boolean;
}

const EduCatalystWidget: React.FC<EduCatalystWidgetProps> = ({ allWorkshops, currentPage, itemsPerPage, onPageChange, handleRegisterWorkshop, submittingWorkshopId, loading }) => {
  // Tính toán các workshop sẽ hiển thị trên trang hiện tại
  const indexOfLastWorkshop = currentPage * itemsPerPage;
  const indexOfFirstWorkshop = indexOfLastWorkshop - itemsPerPage;
  const currentWorkshops = allWorkshops.slice(indexOfFirstWorkshop, indexOfLastWorkshop);

  // Tính toán tổng số trang
  const totalPages = Math.ceil(allWorkshops.length / itemsPerPage);

  // Hàm để hiển thị host một cách thân thiện
  const renderHosts = (hosts: IHost[] | string, hostName: string) => {
    let parsedHosts: IHost[] = [];
    if (hosts && hosts !== "[]") {
      try {
          parsedHosts = typeof hosts === "string" ? JSON.parse(hosts) : hosts;
          if (parsedHosts.length === 1) {
            return parsedHosts[0].host_name;
          }
          return `${parsedHosts[0].host_name} and ${parsedHosts.length - 1} other(s)`;
      } catch (e) {
          console.error("Error parsing hosts string:", e);
      }
    } else if (hostName) {
      return hostName || 'N/A';
    }
  };

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <WidgetHeader title="EduCatalyst" viewAllLink={`https://${process.env.API_HOST}/${process.env.PREFIX_EDUECOSYSTEM}educatalyst`} />

      {loading && allWorkshops.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : allWorkshops && allWorkshops.length > 0 ? (
        <>
          <Box>
            {currentWorkshops.map((workshop: any) => ( // Lặp qua currentWorkshops
              <Card key={workshop.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    {workshop.image_url && (
                      <Box sx={{ mb: 2, borderRadius: '4px', overflow: 'hidden' }}>
                        <img src={workshop.image_url} alt={workshop.title} style={{ width: '100%', height: '150px', objectFit: 'cover' }} />
                      </Box>
                    )}
                    <Typography variant="h6" sx={{ mt: 0, mb: 1, fontWeight: 600 }}>
                      {workshop.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: '40px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {workshop.description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      **Date:** {new Date(workshop.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      **Host:** {renderHosts(workshop.hosts, workshop.host_name)}
                    </Typography>
                    {workshop.topics && workshop.topics.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        {workshop.topics.map((topic: any) => (
                          <Chip key={topic.id} label={topic.name} size="small" sx={{ mr: 1, mb: 1, bgcolor: '#6f42c1', color: 'white' }} />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                  <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
                    <Button
                      variant="contained"
                      // Thay đổi từ href sang onClick
                      onClick={() => handleRegisterWorkshop(workshop)}
                      // isDisabled nếu đã đăng ký hoặc đang submit
                      disabled={workshop.is_registered || submittingWorkshopId == workshop.id}
                      sx={{
                        bgcolor: workshop.is_registered ? '#eee' : '#1976d2',
                        color: workshop.is_registered ? '#000' : '#fff',
                        padding: '8px 15px',
                        borderRadius: '5px',
                        fontWeight: 500,
                        textTransform: 'none',
                        width: '100%',
                        '&:hover': {
                            // Giữ màu giống như khi không hover nếu đã disabled
                            bgcolor: workshop.is_registered ? '#eee' : '#1976d2',
                        },
                        // Thêm kiểu khi disabled
                        '&.Mui-disabled': {
                            bgcolor: "#eee", // Màu nền xám khi disabled
                            color: "#000", // Màu chữ xám khi disabled
                        },
                      }}
                    >
                      {submittingWorkshopId == workshop.id && !workshop.is_registered ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                      {workshop.is_registered ? "Registered" : "Register now"}
                      {/* {!workshop.is_registered && !submittingWorkshopId == workshop.id && <ExternalLink size={16} style={{ marginLeft: 8 }} />} */}
                    </Button>
                  </Box>
              </Card>
            ))}
          </Box>
          {totalPages > 1 && ( // Chỉ hiển thị phân trang nếu có nhiều hơn 1 trang
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={onPageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      ): (
        <Typography variant="body1">No upcoming workshops.</Typography>
      )}
    </Paper>
  );
};

export default function EducatalystWidget() {
  const navigate = useNavigate();
  const { user } = useUser();
  const alert = useAlert();
  const { loadRegisteredWorkshops } = useWorkshop();

  const [loading, setLoading] = useState<boolean>(false);
  const [allWorkshops, setAllWorkshops] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1); // State cho trang hiện tại
  const [itemsPerPage, setItemsPerPage] = useState<number>(1); // State cho số mục trên mỗi trang
  const [submittingWorkshopId, setSubmittingWorkshopId] = useState<string | null>(null);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const loadWorkshop = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiRemote.getNearestWorkshop(user?.user_login, userTimeZone);
      console.log(response);
      if (response.data && Array.isArray(response.data)) {
        setAllWorkshops(response.data);
      } else {
        setAllWorkshops([]);
      }
    } catch (error) {
      console.error("Error:", error);
      setAllWorkshops([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadWorkshop();
  }, [loadWorkshop]);

  const handleRegisterWorkshop = (workshop: any) => {
    setSubmittingWorkshopId(workshop.id);
      apiRemote
        .registerWorkshop({
          username: user?.user_login,
          user_email: user?.user_email,
          workshop_id: workshop.id,
          userTimeZone: userTimeZone
        })
        .then((data) => {
          if (data.code == "201") {
            alert.success(data.message);
            // loadWorkshop();
            setAllWorkshops(prevWorkshops =>
              prevWorkshops.map(w => // Đổi tên biến để tránh nhầm lẫn với workshop từ tham số hàm
                w.id == workshop.id ? { ...w, is_registered: true } : w
              )
            );
            loadRegisteredWorkshops();
          } else {
            alert.error(data.message);
          }
        })
        .catch((err) => {
          alert.error(err.message || "Failed to add new workshop.");
        })
        .finally(() => {
          setSubmittingWorkshopId(null);
        });
  };

  // Hàm xử lý khi thay đổi trang
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    // Cuộn lên đầu trang khi chuyển trang (tùy chọn)
    // window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <ThemeProvider theme={theme}>
      <EduCatalystWidget
        allWorkshops={allWorkshops}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        handleRegisterWorkshop={handleRegisterWorkshop}
        submittingWorkshopId={submittingWorkshopId}
        loading={loading}
      />
    </ThemeProvider>
  );
}